import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

// Tri numericka tekstualna polja (dan / mesec / godina) umesto date pickera.
// Ka roditelju (i time ka backendu) uvek emituje isti format: 'YYYY-MM-DD'
// (ili prazan string kada datum nije kompletan/validan), tako da backend
// ne trpi nikakvu promenu.

const digitsOnly = (str, maxLen) =>
  (str || '').replace(/\D/g, '').slice(0, maxLen);

const pad2 = (num) => String(num).padStart(2, '0');

const isValidIso = (iso) => dayjs(iso, 'YYYY-MM-DD', true).isValid();

const partsToIso = ({ day, month, year }) => {
  if (day.length >= 1 && month.length >= 1 && year.length === 4) {
    const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    return isValidIso(iso) ? iso : '';
  }
  return '';
};

const isoToParts = (value) => {
  const parsed = value ? dayjs(value) : null;
  if (parsed && parsed.isValid()) {
    return {
      day: pad2(parsed.date()),
      month: pad2(parsed.month() + 1),
      year: String(parsed.year()),
    };
  }
  return { day: '', month: '', year: '' };
};

const BirthDateInput = ({ value, onChange, disabled, id }) => {
  const [parts, setParts] = useState(() => isoToParts(value));
  const lastEmittedRef = useRef(value || '');

  // Re-sinhronizuj polja kada se spoljna vrednost promeni na nesto sto nismo
  // upravo mi emitovali (otvaranje modala sa postojecim korisnikom, reset...).
  useEffect(() => {
    if ((value || '') !== lastEmittedRef.current) {
      setParts(isoToParts(value));
      lastEmittedRef.current = value || '';
    }
  }, [value]);

  const updatePart = (key, raw, maxLen) => {
    const next = { ...parts, [key]: digitsOnly(raw, maxLen) };
    setParts(next);
    const iso = partsToIso(next);
    lastEmittedRef.current = iso;
    onChange?.(iso);
  };

  // Ako su sva tri polja popunjena a datum nije validan, oznaci gresku.
  const isComplete =
    parts.day.length >= 1 && parts.month.length >= 1 && parts.year.length === 4;
  const status = isComplete && !partsToIso(parts) ? 'error' : undefined;

  const baseStyle = { textAlign: 'center' };

  return (
    <div className="birthDateInput" style={{ display: 'flex', gap: 8 }}>
      <Input
        id={id}
        inputMode="numeric"
        placeholder="DD"
        maxLength={2}
        disabled={disabled}
        status={status}
        value={parts.day}
        onChange={(e) => updatePart('day', e.target.value, 2)}
        style={{ ...baseStyle, flex: 1 }}
      />
      <Input
        inputMode="numeric"
        placeholder="MM"
        maxLength={2}
        disabled={disabled}
        status={status}
        value={parts.month}
        onChange={(e) => updatePart('month', e.target.value, 2)}
        style={{ ...baseStyle, flex: 1 }}
      />
      <Input
        inputMode="numeric"
        placeholder="GGGG"
        maxLength={4}
        disabled={disabled}
        status={status}
        value={parts.year}
        onChange={(e) => updatePart('year', e.target.value, 4)}
        style={{ ...baseStyle, flex: 1.4 }}
      />
    </div>
  );
};

BirthDateInput.propTypes = {
  // Vrednost u formatu 'YYYY-MM-DD' (ili prazno).
  value: PropTypes.string,
  // Poziva se sa 'YYYY-MM-DD' kada je datum kompletan i validan, inace ''.
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

export default BirthDateInput;
